// src/redux/resource/statusCodeListSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/sif/zivalStatus";

const initialState = {
  list: [],
  page: {},
  byId: {},
  // currently loaded/edited code (sifra)
  details: null, // probably redundant but to be conformed with standard slice structure

  isLoading: false,
  isListLoading: false,
  error: null,
};

/**
 * GET /api/sif/zivalStatus
 * Returns full list of status codes.
 */
export const fetchStatusCodeList = createAsyncThunk(
  "statusCodeLists/fetchAll",
  async (_, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(`${baseUrl}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/sif/zivalStatus/{sifra}
 */
export const fetchStatusCodeBySifra = createAsyncThunk(
  "statusCodeLists/fetchBySifra",
  async (sifra, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(`${baseUrl}/${sifra}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/sif/zivalStatus/page
 * Uses ApiClient paging/filter/sort signature.
 */
export const fetchStatusCodePage = createAsyncThunk(
  "statusCodeLists/fetchPage",
  // { page, filter, sort } – all optional
  async ({ page, filter, sort } = {}, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        true // stripQueryParamsForCancelPrevious
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * POST /api/sif/zivalStatus
 */
export const createStatusCode = createAsyncThunk(
  "statusCodeLists/create",
  async (payload, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.post(`${baseUrl}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * PUT /api/sif/zivalStatus/{sifra}
 */
export const updateStatusCode = createAsyncThunk(
  "statusCodeLists/update",
  async ({ sifra, data }, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.put(`${baseUrl}/${sifra}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * DELETE /api/sif/zivalStatus/{sifra}
 */
export const deleteStatusCode = createAsyncThunk(
  "statusCodeLists/delete",
  async (sifra, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      await ApiClient.delete(`${baseUrl}/${sifra}`);
      return sifra;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const statusCodeListSlice = createSlice({
  name: "statusCodeLists",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ---- LIST (ALL) ----
    builder.addCase(fetchStatusCodeList.pending, (state) => {
      state.isListLoading = true;
      state.error = null;
    });
    builder.addCase(fetchStatusCodeList.fulfilled, (state, action) => {
      state.list = action.payload;
      state.byId = {};
      for (const item of action.payload) {
        if (item?.sifra) {
          state.byId[item.sifra] = item;
        }
      }
      state.isListLoading = false;
    });
    builder.addCase(fetchStatusCodeList.rejected, (state, action) => {
      state.error = action.payload;
      state.isListLoading = false;
    });

    // ---- SINGLE BY SIFRA ----
    builder.addCase(fetchStatusCodeBySifra.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchStatusCodeBySifra.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        state.details = item.sifra;
      }
    });
    builder.addCase(fetchStatusCodeBySifra.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- PAGE ----
    builder.addCase(fetchStatusCodePage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchStatusCodePage.fulfilled, (state, action) => {
      state.isLoading = false;
      state.page = action.payload;
      const content = action.payload?.content || [];
      state.list = content;
      state.byId = {};
      for (const item of content) {
        if (item?.sifra) {
          state.byId[item.sifra] = item;
        }
      }
    });
    builder.addCase(fetchStatusCodePage.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- CREATE ----
    builder.addCase(createStatusCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createStatusCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        state.list.push(item);
        state.details = item.sifra;
      }
    });
    builder.addCase(createStatusCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- UPDATE ----
    builder.addCase(updateStatusCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateStatusCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        const idx = state.list.findIndex((s) => s.sifra === item.sifra);
        if (idx !== -1) {
          state.list[idx] = item;
        }
        state.details = item.sifra;
      }
    });
    builder.addCase(updateStatusCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- DELETE ----
    builder.addCase(deleteStatusCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteStatusCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const sifra = action.payload;
      delete state.byId[sifra];
      state.list = state.list.filter((s) => s.sifra !== sifra);
      if (state.details === sifra) {
        state.details = null;
      }
    });
    builder.addCase(deleteStatusCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
  },
});

export default statusCodeListSlice.reducer;

/**
 * Example response item shape (for reference):
 *
 * {
 *   "sifra": "AKTIVNA",
 *   "naziv": {
 *     "key": "SIF_ZIVAL_STATUS_AKTIVNA",
 *     "sl": "Aktivna",
 *     "en": "Active",
 *     "version": 1,
 *     "createdOn": "2025-12-06T09:55:51.600816Z",
 *     "createdBy": "00000000-0000-0000-0000-000000000000",
 *     "updatedOn": null,
 *     "updatedBy": null
 *   },
 *   "opis": {
 *     "key": "ZIVAL_STATUS_NEAKTIVNA_OPIS",
 *     "sl": "Žival ni več aktivna v reji",
 *     "en": "Animal is no longer active in production",
 *     "version": 1,
 *     "createdOn": "2025-12-04T11:49:38.05696Z",
 *     "createdBy": "00000000-0000-0000-0000-000000000000",
 *     "updatedOn": null,
 *     "updatedBy": null
 *   },
 *   "veljaZaKoze": true,
 *   "veljaZaOvce": true,
 *   "veljaZaKonje": true,
 *   "veljaZaGovedo": true,
 *   "veljaZaPrasice": true,
 *   "veljaZaSamce": true,
 *   "veljaZaSamice": true,
 *   "createdOn": "2025-12-06T09:55:51.600816Z",
 *   "createdBy": null,
 *   "updatedOn": null,
 *   "updatedBy": null,
 *   "version": 1
 * }
 */
